import { Box, Stack, Typography, Button, Drawer } from "@mui/material";
import React, { useState } from "react";
import { Tooltip } from "@mui/material";

const InventoryGrid = ({
  room,
  daysOfMonth,
  startIndex,
  daysPerPage,
  selectedDays,
  setSelectedDays,
  editType,
  setEditType,
  editModeRoom,
  setEditModeRoom,
  isSelecting,
  setIsSelecting,
  selectionStart,
  setSelectionStart,
  editRatePlan,
  setEditRatePlan,
  bulkDrawer,
  setBulkDrawer,
  bulkValue,
  setBulkValue,
  handleApplyBulk,
  setActivePage,
  allRatePlans = [] // ✅ nouveau
}) => {

   const [selectedRatePlanForPrice, setSelectedRatePlanForPrice] = useState(null);

  const labelStyle = {
    backgroundColor: "#F8FAFC",
    borderRight: "1px solid #E2E8F0",
    borderBottom: "1px solid #E2E8F0",
    px: 2,
    py: 2,
    fontWeight: 600,
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const cellStyle = {
    borderLeft: "1px solid #E2E8F0",
    borderBottom: "1px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    fontSize: 13,
    transition: "all 0.15s ease",
    "&:hover": { backgroundColor: "#EEF2FF" }
  };

  const parseDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    return new Date(y, m - 1, d);
  };

  const ratePlans = allRatePlans.map(plan => ({ 
    id: plan._id.toString(), 
    name: plan.name 
  }));

  // ✅ hasPrice basé sur la nouvelle structure
  const hasPrice = room.days?.some(day => day.rates?.some(r => r.price > 0));

  return (
    <Box sx={{ border: "1px solid #E2E8F0", borderRadius: 3, overflow: "hidden", mb: 5 }}>

      {/* ROOM HEADER */}
      <Box sx={{
        backgroundColor: "#F8FAFC",
        px: 3, py: 2,
        borderBottom: "1px solid #E2E8F0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Typography fontWeight={600}>{room.title}</Typography>

        <Box sx={{ display: "flex", gap: 1 }}>

        {/* Pas de prix → seul badge utile ici */}
        {!hasPrice && (
          <Box
            onClick={() => { setBulkDrawer({ open: true, type: "price", roomId: room.roomId }); setBulkValue(""); }}
            sx={{
              px: 2, py: 0.5,
              backgroundColor: "#FEF3C7",
              color: "#92400E",
              borderRadius: 2,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#FDE68A" }
            }}
          >
            ⚡ Aucun prix — Cliquez pour configurer
          </Box>
        )}

      </Box>
      </Box>

      {/* GRID */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: `180px repeat(${daysPerPage}, 1fr)`,
        userSelect: "none"
      }}>

        {/* ═══ RATE PLAN ROWS ═══ */}
        {ratePlans.length === 0 ? (
          <>
            <Box sx={{ ...labelStyle, color: "#94A3B8" }}>Aucun tarif</Box>
            {daysOfMonth.slice(startIndex, startIndex + daysPerPage).map((_, index) => (
              <Box key={index} sx={{ ...cellStyle, backgroundColor: "#F8FAFC", color: "#CBD5E1" }}>
                —
              </Box>
            ))}
          </>
        ) : (
          ratePlans.map(plan => (
            <React.Fragment key={plan.id}>
              <Tooltip label={plan.name}>
                <Box
                  sx={{ ...labelStyle }}
                  onClick={() => {
                    setBulkDrawer({
                      open: true,
                      type: "price",
                      roomId: room.roomId,
                      ratePlanId: plan.id  // ✅ déjà le bon rate plan
                    });
                    setBulkValue("");
                  }}
                >
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {plan.name}
                  </Box>
                  {/* ✅ icône bulk */}
                  <Box sx={{ fontSize: 10, color: "#94A3B8", ml: 1, flexShrink: 0 }}>
                    ✎
                  </Box>
                </Box>
              </Tooltip>

              {daysOfMonth.slice(startIndex, startIndex + daysPerPage).map((dateStr, index) => {
                const dayData = room.days[startIndex + index];
                const rate = dayData?.rates?.find(r => 
                  (r.ratePlanId?._id || r.ratePlanId)?.toString() === plan.id
                );
                const price = rate?.price ?? null;

                const isSelected =
                  selectedDays.tarif?.includes(index) &&
                  editType === "tarif" &&
                  editModeRoom === room.roomId &&
                  editRatePlan === plan.id;

                const isFirst = isSelected && !(selectedDays.tarif?.includes(index - 1));
                const isLast = isSelected && !(selectedDays.tarif?.includes(index + 1));

                return (
                  <Box
                    key={index}
                    title={price === null ? "Cliquez pour définir un prix" : undefined} // ✅ tooltip natif
                    onMouseDown={() => {
                      setIsSelecting(true);
                      setSelectionStart(index);
                      setEditModeRoom(room.roomId);
                      setEditType("tarif");
                      setEditRatePlan(plan.id);
                      setSelectedDays({ tarif: [index], inventory: [], status: [] });
                    }}
                    onMouseEnter={() => {
                      if (isSelecting && editType === "tarif" && editModeRoom === room.roomId && editRatePlan === plan.id && selectionStart !== null) {
                        const start = Math.min(selectionStart, index);
                        const end = Math.max(selectionStart, index);
                        setSelectedDays({ tarif: Array.from({ length: end - start + 1 }, (_, i) => start + i), inventory: [], status: [] });
                      }
                    }}
                    onMouseUp={() => { setIsSelecting(false); setSelectionStart(null); }}
                    sx={{
                      ...cellStyle,
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#4F46E5" : price === null ? "#FEF3C7" : "#FFFFFF",
                      color: isSelected ? "white" : price === null ? "#92400E" : "inherit",
                      borderTopLeftRadius: isFirst ? 8 : 0,
                      borderBottomLeftRadius: isFirst ? 8 : 0,
                      borderTopRightRadius: isLast ? 8 : 0,
                      borderBottomRightRadius: isLast ? 8 : 0,
                    }}
                  >
                    {price !== null ? price : "—"}
                  </Box>
                );
              })}
            </React.Fragment>
          ))
        )}

        {/* ═══ STOCK ROW ═══ */}
        <Box
          sx={labelStyle}
          onClick={() => { setBulkDrawer({ open: true, type: "inventory", roomId: room.roomId }); setBulkValue(""); }}
        >
          Stock
        </Box>

        {daysOfMonth.slice(startIndex, startIndex + daysPerPage).map((_, index) => {
          const dayData = room.days[startIndex + index];
          const available = dayData?.status === "closed" ? 0 : (dayData?.inventory ?? 0);

          const isSelected =
            selectedDays.inventory?.includes(index) &&
            editType === "inventory" &&
            editModeRoom === room.roomId;

          const isFirst = isSelected && !(selectedDays.inventory?.includes(index - 1));
          const isLast = isSelected && !(selectedDays.inventory?.includes(index + 1));

          return (
            <Box
              key={index}
              onMouseDown={() => {
                setIsSelecting(true);
                setSelectionStart(index);
                setEditModeRoom(room.roomId);
                setEditType("inventory");
                setSelectedDays({ tarif: [], inventory: [index], status: [] });
              }}
              onMouseEnter={() => {
                if (isSelecting && editType === "inventory" && editModeRoom === room.roomId && selectionStart !== null) {
                  const start = Math.min(selectionStart, index);
                  const end = Math.max(selectionStart, index);
                  setSelectedDays({ tarif: [], inventory: Array.from({ length: end - start + 1 }, (_, i) => start + i), status: [] });
                }
              }}
              onMouseUp={() => { setIsSelecting(false); setSelectionStart(null); }}
              sx={{
                ...cellStyle,
                cursor: "pointer",
                backgroundColor: isSelected ? "#4F46E5" : available === 0 ? "#FEE2E2" : "#FFFFFF",
                color: isSelected ? "white" : "inherit",
                borderTopLeftRadius: isFirst ? 8 : 0,
                borderBottomLeftRadius: isFirst ? 8 : 0,
                borderTopRightRadius: isLast ? 8 : 0,
                borderBottomRightRadius: isLast ? 8 : 0,
              }}
            >
              {available}
            </Box>
          );
        })}

        {/* ═══ STATUS ROW ═══ */}
        <Box sx={labelStyle}>Statut</Box>

        {daysOfMonth.slice(startIndex, startIndex + daysPerPage).map((_, index) => {
          const dayData = room.days[startIndex + index];
          const status = dayData?.status ?? "closed";
          const isClosed = status === "closed";

          const isSelected =
            selectedDays.status?.includes(index) &&
            editType === "status" &&
            editModeRoom === room.roomId;

          const isFirst = isSelected && !(selectedDays.status?.includes(index - 1));
          const isLast = isSelected && !(selectedDays.status?.includes(index + 1));

          return (
            <Box
              key={index}
              onMouseDown={() => {
                setIsSelecting(true);
                setSelectionStart(index);
                setEditModeRoom(room.roomId);
                setEditType("status");
                setSelectedDays({ tarif: [], inventory: [], status: [index] });
              }}
              onMouseEnter={() => {
                if (isSelecting && editType === "status" && editModeRoom === room.roomId && selectionStart !== null) {
                  const start = Math.min(selectionStart, index);
                  const end = Math.max(selectionStart, index);
                  setSelectedDays({ tarif: [], inventory: [], status: Array.from({ length: end - start + 1 }, (_, i) => start + i) });
                }
              }}
              onMouseUp={() => { setIsSelecting(false); setSelectionStart(null); }}
              sx={{
                ...cellStyle,
                cursor: "pointer",
                fontWeight: 600,
                backgroundColor: isSelected ? "#4F46E5" : isClosed ? "#F1F5F9" : "#DCFCE7",
                color: isSelected ? "white" : isClosed ? "#475569" : "#166534",
                borderTopLeftRadius: isFirst ? 8 : 0,
                borderBottomLeftRadius: isFirst ? 8 : 0,
                borderTopRightRadius: isLast ? 8 : 0,
                borderBottomRightRadius: isLast ? 8 : 0,
              }}
            >
              {isClosed ? "F" : "O"}
            </Box>
          );
        })}
      </Box>

      {/* BULK DRAWER */}
      <Drawer
        anchor="right"
        open={bulkDrawer.open && bulkDrawer.roomId === room.roomId}
        onClose={() => setBulkDrawer({ open: false })}
        PaperProps={{ sx: { width: 380, borderTopLeftRadius: 16, borderBottomLeftRadius: 16 } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

          {/* HEADER */}
          <Box sx={{ p: 3, borderBottom: "1px solid #E2E8F0" }}>
            <Typography variant="h6" fontWeight={600}>
              {bulkDrawer.type === "price" && (
                bulkDrawer.ratePlanId
                  ? `Prix — ${allRatePlans.find(p => p._id.toString() === bulkDrawer.ratePlanId?.toString())?.name}`
                  : "Configurer un prix"
              )}
              {bulkDrawer.type === "inventory" && "Modifier le stock"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Appliquer sur tout le mois affiché
            </Typography>
          </Box>

          {/* CONTENT */}
          <Box sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Sélection rate plan — seulement si pas déjà défini */}
            {bulkDrawer.type === "price" && !bulkDrawer.ratePlanId && (
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#374151" }}>
                  Choisir un tarif
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {allRatePlans.map(plan => (
                    <div
                      key={plan._id}
                      onClick={() => setBulkDrawer(prev => ({ ...prev, ratePlanId: plan._id.toString() }))}
                      style={{
                        padding: "10px 14px",
                        borderRadius: 10,
                        border: bulkDrawer.ratePlanId === plan._id.toString()
                          ? "2px solid #3A9D23"
                          : "1px solid #E2E8F0",
                        backgroundColor: bulkDrawer.ratePlanId === plan._id.toString()
                          ? "#F0FAF0"
                          : "white",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 500
                      }}
                    >
                      {plan.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* INPUT */}
            <input
              type="number"
              placeholder={bulkDrawer.type === "inventory" ? "Nombre de chambres" : "Prix (€)"}
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "1px solid #E2E8F0",
                fontSize: 14,
                outline: "none",
                width: "100%"
              }}
            />

            {/* APERÇU */}
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0" }}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>Aperçu</Typography>
              <Typography fontWeight={600}>
                {bulkDrawer.type === "price" && (
                  bulkDrawer.ratePlanId
                    ? `${allRatePlans.find(p => p._id.toString() === bulkDrawer.ratePlanId?.toString())?.name} → ${bulkValue || 0} € / nuit`
                    : "Sélectionnez un tarif"
                )}
                {bulkDrawer.type === "inventory" && `Stock : ${bulkValue || 0} chambre(s)`}
              </Typography>
            </Box>

            {/* ✅ INDICATION — tooltip sur les cellules jaunes */}
            {bulkDrawer.type === "price" && (
              <Box sx={{
                p: 2, borderRadius: 2,
                backgroundColor: "#EFF6FF",
                border: "1px solid #BFDBFE"
              }}>
                <Typography fontSize={12} color="#1D4ED8">
                  💡 Vous pouvez aussi cliquer sur une cellule jaune dans le calendrier pour modifier le prix d'un jour spécifique.
                </Typography>
              </Box>
            )}

          </Box>

          {/* FOOTER */}
          <Box sx={{ p: 3, borderTop: "1px solid #E2E8F0", display: "flex", gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setBulkDrawer({ open: false })}>
              Annuler
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!bulkValue || (bulkDrawer.type === "price" && !bulkDrawer.ratePlanId)}
              onClick={handleApplyBulk}
            >
              Appliquer
            </Button>
          </Box>

        </Box>
      </Drawer>

    </Box>
  );
};

export default InventoryGrid;